import { useSelector, useDispatch } from 'react-redux'
import type { RootState, AppDispatch } from '../store'
import {
  loginThunk,
  logoutThunk,
  closeAccountThunk,
  disconnectSocialMediaThunk,
  updateUser,
  fetchCurrentUser,
  updateProfileThunk,
  updateSportsThunk,
  updatePreferencesThunk,
  updateSchoolThunk,
  updateAvatarThunk,
  requestMagicLinkThunk,
  verifyMagicLinkThunk,
  SocialAccount,
} from '../redux/userSlice'

/**
 * Auth hook backed by Redux store.
 */
export const useAuth = () => {
  const athlete = useSelector((state: RootState) => state.user.currentUser)
  const isLoading = useSelector((state: RootState) => state.user.loading)
  const profileLoading = useSelector((state: RootState) => state.user.profileLoading)
  const sportsLoading = useSelector((state: RootState) => state.user.sportsLoading)
  const dispatch = useDispatch<AppDispatch>()

  const login = async (creds: { email: string; password: string }) => {
    await dispatch(loginThunk(creds))
  }

  const logoutUser = () => {
    dispatch(logoutThunk())
  }

  const closeAccount = async (reason?: string) => {
    const result = await dispatch(closeAccountThunk(reason))
    if (closeAccountThunk.rejected.match(result)) {
      throw new Error(result.payload as string)
    }
  }

  const connectSocialMedia = (account: SocialAccount) => {
    if (!athlete) return
    dispatch(
      updateUser({
        socialAccounts: [...athlete.socialAccounts, account],
      }),
    )
  }

  const disconnectSocialMedia = async (platform: string) => {
    if (!athlete) return
    const result = await dispatch(disconnectSocialMediaThunk(platform))
    if (disconnectSocialMediaThunk.rejected.match(result)) {
      throw new Error(result.payload as string)
    }
  }

  const updatePreferences = async (field: string, values: string[]) => {
    if (!athlete) return
    await dispatch(updatePreferencesThunk({ field, values })).unwrap()
  }

  const updateSports = async (sports: number[]) => {
    if (!athlete) return
    await dispatch(updateSportsThunk({ sports }))
  }

  const updateSchool = async (schoolId: number | null) => {
    if (!athlete) return
    await dispatch(updateSchoolThunk({ school_id: schoolId })).unwrap()
  }

  const updateProfile = async (profileData: {
    name?: string
    email?: string
    searchable?: boolean
  }) => {
    if (!athlete) return
    await dispatch(updateProfileThunk(profileData)).unwrap()
  }

  const updateAvatar = async (avatarUrl: string) => {
    if (!athlete) return
    await dispatch(updateAvatarThunk({ avatarUrl }))
  }

  const refreshUser = async () => {
    await dispatch(fetchCurrentUser())
  }

  const requestMagicLink = async (email: string) => {
    const result = await dispatch(requestMagicLinkThunk(email))
    if (requestMagicLinkThunk.rejected.match(result)) {
      throw new Error(result.payload as string)
    }
  }

  const verifyMagicLink = async (token: string) => {
    const result = await dispatch(verifyMagicLinkThunk(token))

    if (verifyMagicLinkThunk.rejected.match(result)) {
      throw new Error(result.payload as string)
    }

    // Fetch current user after magic link verification success
    await dispatch(fetchCurrentUser())
  }

  return {
    athlete,
    isLoading,
    profileLoading,
    sportsLoading,
    login,
    logout: logoutUser,
    closeAccount,
    connectSocialMedia,
    disconnectSocialMedia,
    updatePreferences,
    updateSports,
    updateSchool,
    updateProfile,
    updateAvatar,
    refreshUser,
    requestMagicLink,
    verifyMagicLink,
  }
}

// This effect was previously in AuthProvider. It should be moved to a top-level component
// like App.tsx or main.tsx if it needs to run on app mount.
// For now, it's commented out here to avoid breaking changes during migration.
/*
export const useFetchCurrentUserOnMount = () => {
  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    dispatch(fetchCurrentUser());
  }, [dispatch]);
};
*/
